<script setup lang="ts">
  import { computed } from 'vue';
  import { IconImage } from '@/shared/ui';
  import { formatInstantDateToPtBR } from '@/shared/lib';
  import type { TrainingMatrixRevisionInfo } from '@/entities/Training';
  import type { useEditTrainingMatrixRevision } from '../model/useEditTrainingMatrixRevision';
  import { TRAINING_EMPTY_VALUE } from '../lib/trainingEmptyValue';
  import { formatRevisionNumber } from '../lib/formatRevisionNumber';
  import PencilIcon from '@/shared/assets/icons/PencilBlack.svg';
  import SaveIcon from '@/shared/assets/icons/SaveIcon.svg';
  import CloseIcon from '@/shared/assets/icons/CloseIcon.svg';
  import MinusButtonIcon from '@/shared/assets/icons/MinusButtonIcon.svg';
  import PlusButtonIcon from '@/shared/assets/icons/PlusButtonIcon.svg';

  interface Props {
    revision: TrainingMatrixRevisionInfo | null;
    canEdit: boolean;
    edit: ReturnType<typeof useEditTrainingMatrixRevision>;
  }

  const props = defineProps<Props>();

  const revisionNumberLabel = computed(() =>
    props.revision ? formatRevisionNumber(props.revision.revision) : TRAINING_EMPTY_VALUE
  );
  const revisionDateLabel = computed(() =>
    props.revision?.date ? formatInstantDateToPtBR(props.revision.date) : TRAINING_EMPTY_VALUE
  );
  const BLOCKED_NUMBER_KEYS = ['e', 'E', '+', '-', '.', ','];

  function blockNonIntegerKeys(event: KeyboardEvent): void {
    if (BLOCKED_NUMBER_KEYS.includes(event.key)) {
      event.preventDefault();
    }
  }

  function handleRevisionInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    props.edit.setValue(target.value);
    // Vue não reatribui :value quando o ref clampado não muda (ex.: 999 -> 999),
    // então o DOM precisa ser resincronizado manualmente para descartar dígitos extras digitados.
    target.value = String(props.edit.editableValue.value);
  }

  function handleEdit(): void {
    props.edit.startEdit(props.revision?.revision ?? 0);
  }

  function handleCancel(): void {
    props.edit.cancelEdit();
  }

  function handleSave(): void {
    props.edit.requestSave();
  }
</script>

<template>
  <div
    data-testid="training-matrix-revision-control"
    class="flex items-center gap-2 shrink-0"
  >
    <span class="text-sm text-gray-07 whitespace-nowrap">Revisão:</span>

    <template v-if="!edit.isEditing.value">
      <div
        data-testid="training-matrix-revision-number"
        class="bg-gray-09 border border-gray-02 rounded-lg px-4 py-2 text-sm text-gray-03"
      >
        {{ revisionNumberLabel }}
      </div>
      <div
        data-testid="training-matrix-revision-date"
        class="bg-gray-09 border border-gray-02 rounded-lg px-4 py-2 text-sm text-gray-03 whitespace-nowrap"
      >
        {{ revisionDateLabel }}
      </div>
      <button
        v-if="canEdit"
        data-testid="btn-editar-revisao"
        type="button"
        class="bg-[var(--theme-secondary)] rounded-lg w-10.5 h-10.5 flex items-center justify-center shrink-0"
        @click="handleEdit"
      >
        <IconImage
          :src="PencilIcon"
          alt="Editar revisão"
          class="text-white"
        />
      </button>
    </template>

    <template v-else>
      <button
        data-testid="btn-diminuir-revisao"
        type="button"
        :disabled="!edit.canDecrement.value"
        class="size-6 shrink-0 text-[var(--theme-secondary-ink)] disabled:opacity-40 disabled:cursor-not-allowed"
        @click="edit.decrement()"
      >
        <IconImage
          :src="MinusButtonIcon"
          alt="Diminuir revisão"
        />
      </button>
      <input
        data-testid="input-revisao"
        type="number"
        min="1"
        :max="edit.maxRevision"
        step="1"
        class="bg-gray-09 border border-gray-02 rounded-lg px-4 py-2 text-sm text-gray-03 w-16 [appearance:textfield] [-webkit-appearance:textfield] [-moz-appearance:textfield] [&::-webkit-outer-spin-button]:m-0 [&::-webkit-inner-spin-button]:m-0 [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
        :value="edit.editableValue.value"
        @input="handleRevisionInput"
        @keydown="blockNonIntegerKeys"
      >

      <button
        data-testid="btn-aumentar-revisao"
        type="button"
        :disabled="!edit.canIncrement.value"
        class="size-6 shrink-0 text-[var(--theme-secondary-ink)] disabled:opacity-40 disabled:cursor-not-allowed"
        @click="edit.increment()"
      >
        <IconImage
          :src="PlusButtonIcon"
          alt="Aumentar revisão"
        />
      </button>
      <div
        data-testid="training-matrix-revision-date"
        class="bg-gray-09 border border-gray-02 rounded-lg px-4 py-2 text-sm text-gray-03 whitespace-nowrap"
      >
        {{ revisionDateLabel }}
      </div>
      <button
        data-testid="btn-salvar-revisao"
        type="button"
        :disabled="!edit.canSave.value || edit.isSaving.value"
        class="bg-[var(--theme-secondary)] rounded-lg w-10.5 h-10.5 flex items-center justify-center shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
        @click="handleSave"
      >
        <IconImage
          :src="SaveIcon"
          alt="Salvar revisão"
          class="text-white"
        />
      </button>
      <button
        data-testid="btn-cancelar-edicao-revisao"
        type="button"
        class="bg-off-white border border-gray-02 rounded-lg w-10.5 h-10.5 flex items-center justify-center shrink-0"
        @click="handleCancel"
      >
        <IconImage
          :src="CloseIcon"
          alt="Cancelar edição"
        />
      </button>
    </template>
  </div>
</template>
